import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiService } from '../services/api';
import { HorsePricingRead } from '../types/api';

interface HorsePriceDisplayProps {
  horseId: string;
  onPricingChanged?: () => void;
  isEditing?: boolean;
  initialPricing?: HorsePricingRead | null;
}

export const HorsePriceDisplay: React.FC<HorsePriceDisplayProps> = ({ horseId, onPricingChanged, isEditing = false, initialPricing }) => {
  const [pricing, setPricing] = useState<HorsePricingRead | null>(initialPricing ?? null);
  const [loading, setLoading] = useState(initialPricing === undefined);
  const [saving, setSaving] = useState(false);
  const [priceValue, setPriceValue] = useState<string>(
    initialPricing?.price ? Number(initialPricing.price).toLocaleString('en-US') : ''
  );
  const [currencyValue, setCurrencyValue] = useState<string>(initialPricing?.currency || 'EUR');
  const currencySymbols: Record<string, string> = {
    EUR: '€',
    USD: '$',
    GBP: '£',
    CHF: '₣',
  };

  const loadPricing = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiService.getHorsePricing(horseId);
      setPricing(data);
      if (data) {
        setPriceValue(data.price ? Number(data.price).toLocaleString('en-US') : '');
        setCurrencyValue(data.currency || 'EUR');
      } else {
        setPriceValue('');
        setCurrencyValue('EUR');
      }
    } catch (err) {
      // Pricing might not exist, that's okay
      setPricing(null);
      setPriceValue('');
      setCurrencyValue('EUR');
    } finally {
      setLoading(false);
    }
  }, [horseId]);

  const skipInitialLoad = useRef(initialPricing !== undefined);
  useEffect(() => {
    if (skipInitialLoad.current) {
      skipInitialLoad.current = false;
      return;
    }
    loadPricing();
  }, [loadPricing]);

  const _formatPrice = (price: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const persistPricing = useCallback(
    async (nextPrice: string, nextCurrency: string) => {
      const cleaned = nextPrice.replace(/,/g, '');
      const parsedPrice = Number(cleaned);
      if (!cleaned.trim() || Number.isNaN(parsedPrice) || parsedPrice <= 0) {
        return;
      }
      setSaving(true);
      try {
        const payload = {
          price: parsedPrice,
          currency: nextCurrency || 'EUR',
        };
        if (pricing) {
          await apiService.updateHorsePricing(horseId, payload);
        } else {
          await apiService.createHorsePricing(horseId, payload as any);
        }
        await loadPricing();
        onPricingChanged?.();
      } catch (err) {
        console.warn('Failed to save price', err);
      } finally {
        setSaving(false);
      }
    },
    [horseId, pricing, loadPricing, onPricingChanged]
  );

  return (
    <div className="text-sm text-[#233454]">
      {loading ? (
        <p className="flex items-center gap-2 text-gray-500">
          <span className="font-semibold">Price:</span>
          <span className="font-normal">Loading...</span>
        </p>
      ) : isEditing ? (
        /* Edit Mode - inline inputs */
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-[#233454] w-[56px] shrink-0">Price:</span>
          <input
            type="text"
            inputMode="decimal"
            value={priceValue}
            onFocus={() => setPriceValue(priceValue.replace(/,/g, ''))}
            onChange={(e) => {
              const cleaned = e.target.value.replace(/,/g, '');
              setPriceValue(cleaned);
            }}
            onBlur={() => {
              const cleaned = priceValue.replace(/,/g, '');
              if (cleaned) {
                const formatted = Number(cleaned).toLocaleString('en-US');
                setPriceValue(formatted);
                persistPricing(cleaned, currencyValue);
              } else {
                setPriceValue('');
              }
            }}
            className="bg-[#ebf6e4] rounded-[5px] h-[32px] px-3 text-sm text-[#233454] border border-transparent focus:outline-none focus:ring-0 min-w-[140px] max-w-[160px]"
            placeholder="50000"
          />
          <select
            value={currencyValue}
            onChange={(e) => {
              const next = e.target.value;
              setCurrencyValue(next);
              persistPricing(priceValue, next);
            }}
            className="bg-[#ebf6e4] rounded-[5px] h-[32px] px-2 py-1 text-sm text-[#233454] border border-transparent focus:outline-none focus:ring-0 min-w-[64px]"
          >
            {['EUR', 'USD', 'GBP', 'CHF'].map((c) => (
              <option key={c} value={c}>
                {currencySymbols[c] || c}
              </option>
            ))}
          </select>
          {saving && <span className="text-xs text-gray-500">Saving...</span>}
        </div>
      ) : (
        /* View Mode */
        <div className="flex items-start gap-2">
          <span className="text-sm font-semibold text-[#233454] w-[56px] shrink-0">Price:</span>
          <span className="text-sm text-[#233454]">
            {pricing
              ? `${currencySymbols[pricing.currency || 'EUR'] || ''}${Number(pricing.price ?? 0).toLocaleString('en-US')}`
              : '—'}
          </span>
        </div>
      )}
    </div>
  );
};

